// ticketMeta.mjs — the ONE registry of ticket lifecycle metadata on the frontend
// (SB-042). Pure data + pure functions; no React.
//
// Before this module the lifecycle knowledge was scattered across three files
// (STATUS_ORDER in MyTicketsPage, STATUS_COLOR in TicketStatusChip, TICKET_TYPES
// in SubmitTicketPage) plus groupByStatus stranded in a .jsx — the frontend twin
// of the backend's DP-2 "registration surfaces are rarely singular" (the type
// table itself is being unified by SB-046 on the backend side).

// All lifecycle statuses, in a stable grouping order (info-needed first, terminal
// last). Feeds the My-tickets grouping AND the status filter select.
export const STATUS_ORDER = [
  'info_requested',
  'pending',
  'processing',
  'reported',
  'confirmed',
  'handling',
  // SB-190 §3.1 (batch 245): the ASK- A machine's two live states sit with the
  // other active ones — `assigned` is "someone owes an answer", `responded` is
  // "answered, awaiting the issuer's close".
  'assigned',
  'responded',
  'accepted',
  'reviewed',
  'closed',   // SB-190: the A machine's only exit (terminal, archivable)
  'published',
  'rejected',
  'withdrawn',
];

// SB-190 §3.1 (batch 247 review): the A machine's statuses — the three that ONLY an
// ASK- ticket can be in (ticket_store.cpp: assigned → responded → closed, `closed` is
// that machine's only exit and no T/I transition writes it). The approver queue
// excludes the A machine server-side (routes_approver.cpp exclude_machine), so a
// status filter on /approvals offering these three is an always-empty option — the
// same thing the batch kept 'assigned' out of that page's TYPE_ORDER for.
// Derived, not duplicated: every entry must also be in STATUS_ORDER (assignedSurface.test).
export const ASSIGNED_STATUSES = ['assigned', 'responded', 'closed'];

// status → MUI Chip color. Kept exhaustive over the known lifecycle so a status
// never falls through to "default" unless the backend adds a new one.
export const STATUS_COLOR = {
  pending: 'default',
  processing: 'info',
  accepted: 'success',
  rejected: 'error',
  info_requested: 'warning',
  withdrawn: 'default',
  reported: 'info',
  confirmed: 'info',
  handling: 'warning',
  reviewed: 'success',
  published: 'success',
  // SB-190: warning = a clock is running on a named responder (same reading as
  // info_requested); info = answered; success = closed by the issuer side.
  assigned: 'warning',
  responded: 'info',
  closed: 'success',
};

// SB-032/OQ-B19: `self_intro` rides the same rail on purpose — the existing
// request_info/resubmit loop IS the layered AI grill the ruling calls for.
// OQ-C10 ruled the type table extensible (backend unification: SB-046).
// 'asset' (AST-, SB-025 reactivation 2026-07-29): sample submissions with
// attachments; associated with a requesting BL- by a body-header convention
// (the submit form writes it), NOT by the OQ-C17 spec link.
// 'assigned' (ASK-, SB-190 batch 245): the response-clock carrier — an approver
// names a responder at submit time; runs its own A machine (assigned → responded
// → closed). Order = the backend registry order (helpContent.test.mjs pins it).
export const TICKET_TYPES = ['bug', 'incident', 'maintenance', 'tech_debt', 'spec_change', 'self_intro', 'asset', 'assigned'];

// Bucket tickets by status in lifecycle order; sort each bucket by updatedAtMs
// desc. Unknown statuses are appended in first-seen order after the known ones.
export function groupByStatus(tickets) {
  const buckets = new Map();
  for (const tk of tickets || []) {
    if (!buckets.has(tk.status)) buckets.set(tk.status, []);
    buckets.get(tk.status).push(tk);
  }
  const ordered = [];
  const seen = new Set();
  for (const s of STATUS_ORDER) {
    if (buckets.has(s)) {
      ordered.push(s);
      seen.add(s);
    }
  }
  for (const s of buckets.keys()) if (!seen.has(s)) ordered.push(s);
  return ordered.map((status) => ({
    status,
    rows: buckets.get(status).sort((a, b) => (b.updatedAtMs || 0) - (a.updatedAtMs || 0)),
  }));
}
